import { defineAsyncComponent, markRaw } from 'vue'
import { createApp, type ModuleConfig } from '@bridge2ai/core/frontend'
import config from './config/modules.config.json'
import terminology from '../../terminology.json'
import EmsImportWindow from './components/EmsImportWindow.vue'
import { fssQuoteModule } from './modules/ems-quote/module'

// Tenant-local modules (see modules/client-cost-report, modules/odoo-sync-monitor).
const customModules: ModuleConfig[] = [
  fssQuoteModule,
  {
    id: 'costReport',
    moduleId: 'cost-report',
    slug: 'cost-report',
    name: 'Cost Report',
    iconUrl: '/icons/cost-report.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/client-cost-report/ClientCostReport.vue')),
  },
  {
    id: 'syncMonitor',
    moduleId: 'sync-monitor',
    slug: 'sync-monitor',
    name: 'Odoo Sync',
    iconUrl: '/icons/sync-monitor.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/odoo-sync-monitor/SyncMonitor.vue')),
  },
  {
    id: 'ftrackActuals',
    moduleId: 'ftrack-report',
    slug: 'ftrack-actuals',
    name: 'Actuals',
    iconUrl: '/icons/ftrack.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/ftrack-actuals/FtrackActuals.vue')),
  },
]

createApp({
  tenantId: 'foreseeson',
  config: config as any,
  customModules,
  // Tenant UI pack: 1EMS vocabulary + the EMS fields the scheduler sync
  // attaches under ext.* — surfaced as first-class, filterable columns.
  tenant: {
    apiVersion: 1,
    terminology: terminology as any,
    // The Import Data modal content is tenant-owned: EmsImportWindow drives
    // the manual 1EMS/Odoo sync + optimizer (replaces Core's Epicor-shaped
    // default window entirely). Core's header Sync button and
    // sync-and-optimize also go through the ems-jobs adapter.
    sync: { label: 'Sync 1EMS', adapter: 'ems-jobs' },
    components: {
      importWindow: markRaw(EmsImportWindow),
    },
    fieldLabels: {
      jobNum: 'MO',
      soId: 'PO',
    },
    fields: [
      { key: 'extManager', source: 'ext.manager', label: 'Manager', entity: 'job', type: 'enum' },
      { key: 'extPjtCode', source: 'ext.pjtCode', label: 'Project', entity: 'job', type: 'string' },
      { key: 'extCustPo', source: 'ext.custPo', label: 'Customer PO#', entity: 'job', type: 'string' },
      { key: 'extTitle', source: 'ext.title', label: 'MO Title', entity: 'job', type: 'string' },
    ],
    // Material-tracking fields — Foreseeson has no materials source (no QOH /
    // time-phase feed), so these would render as permanently-empty columns.
    hiddenFields: ['rfeh', 'partsSummary', 'materialText', 'numLines', 'earliestStart'],
    // Managed dashboard views: definitions live HERE, re-applied on every
    // load. "All MOs" is the immutable default; users can add their own
    // views next to these.
    views: [
      {
        name: 'All MOs',
        isDefault: true,
        color: 'slate-blue',
        columns: ['jobNum', 'extTitle', 'extPjtCode', 'extManager', 'extCustPo', 'currentStart', 'issues'],
        sorts: [{ field: 'daysLate', dir: 'desc' }],
        detailFields: ['partNumber', 'estimatedHours', 'salesOrderShipDate'],
      },
      {
        name: 'Issues',
        color: 'clay',
        columns: ['jobNum', 'extTitle', 'extPjtCode', 'currentStart', 'issues'],
        filters: [{ field: 'hasAnyIssue', op: 'is', values: [true] }],
        sorts: [{ field: 'daysLate', dir: 'desc' }],
        detailFields: ['partNumber', 'extManager', 'salesOrderShipDate'],
      },
      {
        name: 'Customer POs',
        list: 'salesOrders',
        color: 'sage',
        columns: ['jobNum', 'extTitle', 'currentStart', 'issues'],
        detailFields: ['partNumber', 'extPjtCode'],
      },
    ],
  },
})
